import { Trend, TrendCompany, TrendTopic } from 'api/model'
import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import {
  trendsGetCompanies,
  trendsGetTopicTypes,
  trendsGetTopics,
  trendsListTrends,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'

function useFetchTrends(twinId: string, companyName: string, topicType: string) {
  const [activeOrganization] = useActiveOrganization()
  const [trends, setTrends] = useState<Trend[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchTrends = useCallback(async () => {
    if (!activeOrganization || !twinId || !topicType) {
      return
    }

    setIsLoading(true)

    try {
      const response = await trendsListTrends(activeOrganization, twinId, {
        company: companyName,
        topic_type: topicType,
      })
      if (response.data) {
        setTrends(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId, companyName, topicType])

  useLayoutEffect(() => {
    refetchTrends()
  }, [refetchTrends])

  return {
    isLoading,
    trends,
    error,
  }
}

function useFetchCompanies(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [companies, setCompanies] = useState<TrendCompany[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchInsightCategories = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await trendsGetCompanies(activeOrganization, twinId)
      if (response.data) {
        setCompanies(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId])

  useLayoutEffect(() => {
    refetchInsightCategories()
  }, [refetchInsightCategories])

  return {
    isLoading,
    companies,
    error,
  }
}

function useFetchTrendTopics(twinId: string, companyName?: string) {
  const [activeOrganization] = useActiveOrganization()
  const [trendTopics, setTrendTopics] = useState<TrendTopic[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchTrendTopics = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await trendsGetTopics(activeOrganization, twinId, {
        company: companyName || undefined,
      })
      if (response.data) {
        setTrendTopics(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId, companyName])

  useLayoutEffect(() => {
    refetchTrendTopics()
  }, [refetchTrendTopics])

  return {
    isLoading,
    trendTopics,
    error,
    refetchTrendTopics,
  }
}

// this function is used to fetch the topics that does have trend data
function useFetchTrendsTopicTypes(twinId: string, companyName: string) {
  const [activeOrganization] = useActiveOrganization()
  const [trendsTopicTypes, setTrendsTopicTypes] = useState<string[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchInsightCategories = useCallback(async () => {
    if (!activeOrganization) {
      return
    }

    setIsLoading(true)

    try {
      const response = await trendsGetTopicTypes(activeOrganization, twinId, { company: companyName })
      if (response.data) {
        setTrendsTopicTypes(response.data)
      }
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId, companyName])

  useLayoutEffect(() => {
    refetchInsightCategories()
  }, [refetchInsightCategories])

  return {
    isLoading,
    trendsTopicTypes,
    error,
  }
}

export { useFetchCompanies, useFetchTrends, useFetchTrendsTopicTypes, useFetchTrendTopics }
